  const top3DGradients = (
    <defs>
      <linearGradient id="topSku3dFront" x1="0" y1="0" x2="1" y2="1">
        <stop offset="0%" stopColor="#8b5cf6" stopOpacity={1}/>
        <stop offset="50%" stopColor="#7c3aed" stopOpacity={0.95}/>
        <stop offset="100%" stopColor="#5b21b6" stopOpacity={0.85}/>
      </linearGradient>
      <linearGradient id="topSku3dSide" x1="0" y1="0" x2="1" y2="0">
        <stop offset="0%" stopColor="#5b21b6" stopOpacity={0.7}/>
        <stop offset="100%" stopColor="#3b1a75" stopOpacity={0.9}/>
      </linearGradient>
      <filter id="topSkuShadow3D" x="-50%" y="-50%" width="200%" height="200%">
        <feGaussianBlur in="SourceAlpha" stdDeviation="4"/>
        <feOffset dx="3" dy="6" result="offsetblur"/>
        <feComponentTransfer>
          <feFuncA type="linear" slope="0.4"/>
        </feComponentTransfer>
        <feMerge>
          <feMergeNode/>
          <feMergeNode in="SourceGraphic"/>
        </feMerge>
      </filter>
      <radialGradient id="topSkuGloss" cx="50%" cy="0%" r="50%">
        <stop offset="0%" stopColor="#ede9fe" stopOpacity={0.35}/>
        <stop offset="100%" stopColor="#7c3aed" stopOpacity={0}/>
      </radialGradient>
    </defs>
  );
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { BarChart3, ArrowLeft, Search, RotateCcw, TrendingUp, PieChart, Activity } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { useLanguage } from '@/contexts/LanguageContext';
import { ROUTES } from '@/lib/routes';
import type { CategoryKey } from '@/lib/navigation';
import { supabase } from '@/lib/supabaseClient';
import { ensureAuthReady } from '@/lib/ensureAuthReady';
import { toast } from 'sonner';
import { companyService } from '@/services/companyService';
import { triggerReloadWithSalesAnalysisFilters, pickReloadSearch } from '@/utils/reloadSearch';
import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
  ChartLegend,
  ChartLegendContent
} from '@/components/ui/chart';
import { BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend, Cell } from 'recharts';

interface SalesOrderRow {
  id: number;
  order_id: string;
  order_date: string | null;
  order_time: string | null;
  sku_id: string;
  company_sku_key: string | null;
  sale_quantity: number;
  sale_unit_price: number;
  total_amount: number | null;
  snapshot_net_revenue: number | null;
  snapshot_gross_profit: number | null;
  snapshot_cost_unit: number | null;
  sale_commission_rate: number | null;
  sale_sku_brand_en: string | null;
  sale_sku_brand_zh: string | null;
  sale_sub_category_code: string | null;
  sale_sku_name_en: string | null;
  sale_sku_name_zh: string | null;
}

interface ProcessedRow extends SalesOrderRow {
  productName: string;
  brand: string;
  netRevenue: number;
  grossProfitValue: number | null;
  gmv: number;
}

interface SearchFilters {
  startDate: string;
  endDate: string;
  skuId: string;
  brand: string;
  categoryCode: string;
  productName: string;
}

const PRICE_BANDS = [
  { label: '$0-$99', min: 0, max: 99 },
  { label: '$100-$199', min: 100, max: 199 },
  { label: '$200-$299', min: 200, max: 299 },
  { label: '$300-$399', min: 300, max: 399 },
  { label: '$400-$499', min: 400, max: 499 },
  { label: '$500-$599', min: 500, max: 599 },
  { label: '$600-$699', min: 600, max: 699 },
  { label: 'over $700', min: 700, max: Infinity }
];

const PROFIT_BANDS = [
  { label: '$0-$19', min: 0, max: 19 },
  { label: '$20-$49', min: 20, max: 49 },
  { label: '$50-$79', min: 50, max: 79 },
  { label: '$80-$119', min: 80, max: 119 },
  { label: '$120-$159', min: 120, max: 159 },
  { label: '$160-$209', min: 160, max: 209 },
  { label: '$210-$299', min: 210, max: 299 },
  { label: 'over $300', min: 300, max: Infinity }
];

const ANALYTICS_CATEGORY: CategoryKey = 'analytics';

const SalesAnalysis: React.FC = () => {
  const navigate = useNavigate();
  const { language } = useLanguage();

  const [currentCompany, setCurrentCompany] = useState<{ id: string; code?: string; name?: string } | null>(null);
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState<ProcessedRow[]>([]);
  const [topSkuMetric, setTopSkuMetric] = useState<'gmv' | 'grossProfit'>('gmv');
  const [topBrandMetric, setTopBrandMetric] = useState<'gmv' | 'grossProfit'>('gmv');
  const [priceBandMetric, setPriceBandMetric] = useState<'price' | 'profit'>('price');
  const [categoryNames, setCategoryNames] = useState<Record<string, { subCategoryName: string | null; categoryBrandName: string | null }>>({});

  // 暫存待回放搜尋條件，確保公司資訊就緒後仍能執行
  const pendingReloadRef = useRef<ReturnType<typeof pickReloadSearch>>(null);

  const RANK_COLORS = ['#8b5cf6', '#22d3ee', '#f59e0b', '#10b981', '#ef4444', '#6366f1', '#14b8a6', '#ec4899', '#a3e635', '#f97316'];

  const renderTopSkuTooltip = useCallback(
    ({ active, payload }: any) => {
      if (!active || !payload?.length) return null;
      const p = payload[0]?.payload;
      if (!p) return null;
      const metricLabel = topSkuMetric === 'gmv' ? (language === 'zh' ? '交易總額' : 'GMV') : (language === 'zh' ? '利潤' : 'Profit');
      const metricValue = topSkuMetric === 'gmv' ? p.gmv : p.grossProfit;
      return (
        <div className="rounded-md border border-slate-700 bg-slate-900 px-3 py-2 text-xs text-slate-100 shadow-lg">
          <div className="font-semibold mb-1" style={{ color: p.color }}>{p.name}</div>
          <div className="text-slate-300">SKU: {p.sku}</div>
          <div className="text-slate-300">{language === 'zh' ? '品牌' : 'Brand'}: {p.brand}</div>
          <div className="text-slate-300">{language === 'zh' ? '總數量' : 'Qty'}: {p.quantity}</div>
          <div className="text-slate-100">{metricLabel}: ${metricValue?.toFixed(2) ?? '0.00'}</div>
        </div>
      );
    },
    [language, topSkuMetric]
  );

  // Abort control to avoid overlapping queries
  const ctrlRef = useRef<AbortController | null>(null);

  const getToday = () => new Date().toISOString().split('T')[0];
  const getFirstDayOfMonth = () => {
    const date = new Date();
    date.setDate(1);
    return date.toISOString().split('T')[0];
  };

  const [filters, setFilters] = useState<SearchFilters>({
    startDate: getFirstDayOfMonth(),
    endDate: getToday(),
    skuId: '',
    brand: '',
    categoryCode: '',
    productName: ''
  });

  useEffect(() => {
    const initializeCompany = async () => {
      try {
        const company = await companyService.getCurrentCompany();
        if (company) setCurrentCompany({ id: String(company.id), code: company.code, name: company.name });
      } catch (error) {
        console.error('Failed to get company:', error);
        toast.error(language === 'zh' ? '獲取公司資訊失敗' : 'Failed to get company information');
      }
    };
    initializeCompany();
  }, [language]);

  // 首次載入時讀取回放條件並保存
  useEffect(() => {
    const pending = pickReloadSearch();
    if (!pending) return;

    pendingReloadRef.current = pending;

    // 立即保存待回放的條件到 UI 狀態，避免遺失
    console.log('🔄 保存待回放搜尋條件到 UI 狀態');
    setFilters(pending.filters);
  }, []);

  // 公司資訊就緒後回放搜尋
  useEffect(() => {
    if (!currentCompany?.id) {
      console.log('🔄 等待公司資訊就緒...');
      return;
    }

    const pending = pendingReloadRef.current;
    if (!pending) return;

    const executePendingSearch = async () => {
      console.log('🔄 檢測到待回放的搜索，開始執行');
      try {
        // 保險：確保 Auth ready 再搜
        await ensureAuthReady();
        await fetchData(pending.filters);
        toast.success(language === 'zh' ? '搜索條件已恢復並執行' : 'Search conditions restored and executed');
      } catch (error) {
        console.error('回放搜索失敗:', error);
        toast.error(language === 'zh' ? '搜索恢復失敗，請重新搜尋' : 'Search restore failed, please search again');
      } finally {
        pendingReloadRef.current = null;
      }
    };

    executePendingSearch();
  }, [currentCompany, language]); // 移除 fetchData 依賴避免無限循環

  const buildQuery = (searchFilters: SearchFilters, companyId: number) => {
    let query = supabase
      .from('sales_orders')
      .select(
        `
        id,
        order_id,
        order_date,
        order_time,
        sku_id,
        company_sku_key,
        sale_quantity,
        sale_unit_price,
        total_amount,
        snapshot_net_revenue,
        snapshot_gross_profit,
        snapshot_cost_unit,
        sale_commission_rate,
        sale_sku_brand_en,
        sale_sku_brand_zh,
        sale_sub_category_code,
        sale_sku_name_en,
        sale_sku_name_zh
      `
      )
      .eq('company_id', companyId)
      .limit(20000); // safeguard to avoid excessive payload

    if (searchFilters.startDate) query = query.gte('order_date', searchFilters.startDate);
    if (searchFilters.endDate) query = query.lte('order_date', searchFilters.endDate);
    if (searchFilters.skuId) query = query.or(`sku_id.ilike.%${searchFilters.skuId}%,company_sku_key.ilike.%${searchFilters.skuId}%`);
    if (searchFilters.brand) query = query.or(`sale_sku_brand_en.ilike.%${searchFilters.brand}%,sale_sku_brand_zh.ilike.%${searchFilters.brand}%`);
    if (searchFilters.categoryCode) query = query.ilike('sale_sub_category_code', `%${searchFilters.categoryCode}%`);
    if (searchFilters.productName) query = query.or(`sale_sku_name_en.ilike.%${searchFilters.productName}%,sale_sku_name_zh.ilike.%${searchFilters.productName}%`);

    return query.order('order_date', { ascending: true }).order('order_time', { ascending: true });
  };

  // 分批取數，避免 Supabase 預設 1,000 筆限制
  const fetchAllData = async <T,>(
    baseQuery: any,
    signal: AbortSignal,
    batchSize: number = 1000,
    maxRows: number = 20000
  ): Promise<T[]> => {
    let all: T[] = [];
    let start = 0;
    let hasMore = true;

    while (hasMore) {
      if (signal.aborted) throw new DOMException('Aborted', 'AbortError');
      const { data, error } = await baseQuery.range(start, start + batchSize - 1).abortSignal(signal);
      if (error) throw error;
      if (data && data.length) {
        all = all.concat(data as T[]);
        start += batchSize;
        hasMore = data.length === batchSize && all.length < maxRows;
      } else {
        hasMore = false;
      }
      if (all.length >= maxRows) {
        hasMore = false;
      }
    }
    return all;
  };

  const fetchData = useCallback(
    async (searchFilters: SearchFilters) => {
      if (!currentCompany?.id) {
        toast.error(language === 'zh' ? '缺少公司資訊' : 'Missing company info');
        return;
      }

      await ensureAuthReady();
      ctrlRef.current?.abort();
      const controller = new AbortController();
      ctrlRef.current = controller;
      const { signal } = controller;

      setLoading(true);
      try {
        const query = buildQuery(searchFilters, Number(currentCompany.id));
        const data = await fetchAllData<SalesOrderRow>(query, signal);

        const processed: ProcessedRow[] = data.map((row: SalesOrderRow) => {
          const brand = row.sale_sku_brand_zh || row.sale_sku_brand_en || '-';
          const productName = row.sale_sku_name_zh || row.sale_sku_name_en || `SKU: ${row.sku_id}`;
          const gmv = row.total_amount ?? 0;
          const netRevenue = row.snapshot_net_revenue ?? gmv;
          const grossProfitValue = row.snapshot_gross_profit ?? null;
          return {
            ...row,
            brand,
            productName,
            netRevenue,
            grossProfitValue,
            gmv
          };
        });

        setRows(processed);
        if (processed.length === 0) {
          toast.info(language === 'zh' ? '沒有符合條件的記錄' : 'No records found');
        }
      } catch (err: any) {
        if (err?.name === 'AbortError') return;
        console.error('Fetch sales analysis error:', err);
        toast.error(language === 'zh' ? '查詢失敗' : 'Failed to fetch data');
      } finally {
        if (!signal.aborted) setLoading(false);
      }
    },
    [currentCompany?.id, language]
  );

  const clearFilters = () => {
    setFilters({
      startDate: getFirstDayOfMonth(),
      endDate: getToday(),
      skuId: '',
      brand: '',
      categoryCode: '',
      productName: ''
    });
    setRows([]);
  };

  // 天數計算（用於日均訂單數）
  const daySpan = useMemo(() => {
    if (!filters.startDate || !filters.endDate) return 0;
    const start = new Date(filters.startDate);
    const end = new Date(filters.endDate);
    const diff = end.getTime() - start.getTime();
    if (Number.isNaN(diff)) return 0;
    return Math.max(1, Math.floor(diff / (1000 * 60 * 60 * 24)) + 1); // 含起迄日
  }, [filters.startDate, filters.endDate]);

  const aggregations = useMemo(() => {
    if (rows.length === 0) {
      return {
        totalAmount: 0,
        netRevenue: 0,
        grossProfit: 0,
        orderCount: 0,
        quantity: 0,
        profitPerUnit: 0,
        profitPerOrder: 0,
        avgOrdersPerDay: 0,
        grossProfitAvailability: 0
      };
    }

    const totalAmount = rows.reduce((sum, r) => sum + (r.total_amount ?? 0), 0);
    const netRevenue = rows.reduce((sum, r) => sum + (r.netRevenue ?? 0), 0);
    const grossProfitSum = rows.reduce((sum, r) => sum + (r.grossProfitValue ?? 0), 0);
    const grossProfitAvailability = rows.filter(r => r.grossProfitValue !== null).length / rows.length;
    const quantity = rows.reduce((sum, r) => sum + (r.sale_quantity ?? 0), 0);
    const orderCount = new Set(rows.map(r => r.order_id)).size;
    const profitPerOrder = orderCount > 0 ? grossProfitSum / orderCount : 0;
    const profitPerUnit = quantity > 0 ? grossProfitSum / quantity : 0;
    const avgOrdersPerDay = daySpan > 0 ? orderCount / daySpan : 0;

    return {
      totalAmount,
      netRevenue,
      grossProfit: grossProfitSum,
      orderCount,
      quantity,
      profitPerOrder,
      profitPerUnit,
      avgOrdersPerDay,
      grossProfitAvailability
    };
  }, [rows, daySpan]);

  const trendData = useMemo(() => {
    const getDateKey = (r: SalesOrderRow) => r.order_date;

    const map = new Map<string, { grossProfit: number; orderCount: number }>();
    rows.forEach(r => {
      const date = getDateKey(r);
      if (!date) return;
      if (!map.has(date)) map.set(date, { grossProfit: 0, orderCount: 0 });
      const bucket = map.get(date)!;
      bucket.grossProfit += r.grossProfitValue ?? 0;
      bucket.orderCount += 1;
    });
    return Array.from(map.entries())
      .map(([date, val]) => ({ date, ...val }))
      .sort((a, b) => a.date.localeCompare(b.date));
  }, [rows]);

  const aggregateByKey = (keyGetter: (r: ProcessedRow) => string | null, topN: number, metric: 'netRevenue' | 'grossProfit' = 'netRevenue') => {
    const map = new Map<string, { netRevenue: number; quantity: number; grossProfit: number }>();
    rows.forEach(r => {
      const key = keyGetter(r);
      if (!key) return;
      if (!map.has(key)) map.set(key, { netRevenue: 0, quantity: 0, grossProfit: 0 });
      const bucket = map.get(key)!;
      bucket.netRevenue += r.netRevenue ?? 0;
      bucket.quantity += r.sale_quantity ?? 0;
      bucket.grossProfit += r.grossProfitValue ?? 0;
    });
    return Array.from(map.entries())
      .map(([name, v]) => ({ name, ...v }))
      .sort((a, b) => b[metric] - a[metric])
      .slice(0, topN);
  };

  const topSkuData = useMemo(() => {
    const map = new Map<string, { sku: string; name: string; brand: string; quantity: number; netRevenue: number; grossProfit: number; gmv: number }>();
    rows.forEach(r => {
      const key = r.sku_id;
      if (!key) return;
      if (!map.has(key)) {
        map.set(key, {
          sku: r.sku_id,
          name: r.productName,
          brand: r.brand,
          quantity: 0,
          netRevenue: 0,
          grossProfit: 0,
          gmv: 0,
        });
      }
      const bucket = map.get(key)!;
      bucket.quantity += r.sale_quantity ?? 0;
      bucket.netRevenue += r.netRevenue ?? 0;
      bucket.grossProfit += r.grossProfitValue ?? 0;
      bucket.gmv += r.gmv ?? 0;
    });
    return Array.from(map.values())
      .sort((a, b) => (topSkuMetric === 'gmv' ? b.gmv - a.gmv : b.grossProfit - a.grossProfit))
      .slice(0, 10)
      .map((item, idx) => ({
        rank: idx + 1,
        color: RANK_COLORS[idx % RANK_COLORS.length],
        ...item,
        value: topSkuMetric === 'gmv' ? item.gmv : item.grossProfit,
      }));
  }, [rows, topSkuMetric]);

  const topBrandData = useMemo(() => {
    const map = new Map<string, { name: string; quantity: number; netRevenue: number; grossProfit: number; gmv: number }>();
    rows.forEach(r => {
      const key = r.brand || '-';
      if (!map.has(key)) {
        map.set(key, { name: key, quantity: 0, netRevenue: 0, grossProfit: 0, gmv: 0 });
      }
      const bucket = map.get(key)!;
      bucket.quantity += r.sale_quantity ?? 0;
      bucket.netRevenue += r.netRevenue ?? 0;
      bucket.grossProfit += r.grossProfitValue ?? 0;
      bucket.gmv += r.gmv ?? 0;
    });
    return Array.from(map.values())
      .sort((a, b) => (topBrandMetric === 'gmv' ? b.gmv - a.gmv : b.grossProfit - a.grossProfit))
      .slice(0, 5)
      .map((item, idx) => ({
        rank: idx + 1,
        color: RANK_COLORS[idx % RANK_COLORS.length],
        ...item,
        value: topBrandMetric === 'gmv' ? item.gmv : item.grossProfit,
      }));
  }, [rows, topBrandMetric]);

  // Category names loader
  useEffect(() => {
    const fetchCategoryNames = async () => {
      try {
        const controller = new AbortController();
        const baseQuery = supabase
          .from('categories')
          .select('category_code, sub_category_name, category_brand_name');

        const data = await fetchAllData<any>(baseQuery, controller.signal, 1000, 20000);
        const map: Record<string, { subCategoryName: string | null; categoryBrandName: string | null }> = {};
        data?.forEach((c: any) => {
          if (c.category_code) {
            map[c.category_code] = {
              subCategoryName: c.sub_category_name ?? null,
              categoryBrandName: c.category_brand_name ?? null
            };
          }
        });
        setCategoryNames(map);
      } catch (e) {
        console.warn('Failed to load category names', e);
      }
    };
    fetchCategoryNames();
  }, []);

  // Product category mapping loader - maps company_sku_key to category_code
  const [productCategoryMap, setProductCategoryMap] = useState<Record<string, string | null>>({});

  useEffect(() => {
    const fetchProductCategoryMapping = async () => {
      if (!currentCompany?.id) return;

      try {
        const controller = new AbortController();

        // Load products with their category_code by company_sku_key
        const baseQuery = supabase
          .from('products')
          .select('company_sku_key, category_code')
          .eq('company_id', currentCompany.id)
          .not('company_sku_key', 'is', null);

        const data = await fetchAllData<any>(baseQuery, controller.signal, 1000, 20000);

        const map: Record<string, string | null> = {};
        data?.forEach((p: any) => {
          if (p.company_sku_key) {
            map[p.company_sku_key] = p.category_code ?? null;
          }
        });
        setProductCategoryMap(map);
      } catch (e) {
        console.warn('Failed to load product category mapping', e);
      }
    };

    fetchProductCategoryMapping();
  }, [currentCompany?.id]);

  // Top 10 categories by sold quantity based on category_code resolved via company_sku_key
  const topCategoryData = useMemo(() => {
    const categoryTotals = new Map<string, { code: string; name: string | null; subCategoryName: string | null; quantity: number }>();

    rows.forEach(r => {
      const companySkuKey = r.company_sku_key || '';
      if (!companySkuKey) return;

      const categoryCode = productCategoryMap[companySkuKey];
      if (!categoryCode) return;

      const categoryInfo = categoryNames[categoryCode];
      const categoryName = categoryInfo?.categoryBrandName ?? null;
      const subCategoryName = categoryInfo?.subCategoryName ?? null;
      if (!categoryTotals.has(categoryCode)) {
        categoryTotals.set(categoryCode, { code: categoryCode, name: categoryName, subCategoryName: subCategoryName, quantity: 0 });
      }

      categoryTotals.get(categoryCode)!.quantity += r.sale_quantity ?? 0;
    });

    return Array.from(categoryTotals.values())
      .sort((a, b) => b.quantity - a.quantity)
      .slice(0, 10)
      .map((item, idx) => ({
        rank: idx + 1,
        color: RANK_COLORS[idx % RANK_COLORS.length],
        code: item.code,
        count: item.quantity,
        quantity: item.quantity,
        name: item.name,
        subCategoryName: item.subCategoryName,
      }));
  }, [rows, productCategoryMap, categoryNames]);
  const topCategory = useMemo(
    () => aggregateByKey((r) => r.sale_sub_category_code || null, 10),
    [rows]
  );

  const priceBandData = useMemo(() => {
    const bands = (priceBandMetric === 'price' ? PRICE_BANDS : PROFIT_BANDS).map(b => ({
      ...b,
      gmv: 0,
      quantity: 0,
      profit: 0
    }));

    rows.forEach(r => {
      const value = priceBandMetric === 'price' ? (r.sale_unit_price ?? 0) : (r.grossProfitValue ?? 0);
      const band = bands.find(b => value >= b.min && value <= b.max);
      if (!band) return;
      band.gmv += r.gmv ?? 0;
      band.quantity += r.sale_quantity ?? 0;
      band.profit += r.grossProfitValue ?? 0;
    });
    return bands;
  }, [rows, priceBandMetric]);

  const grossProfitMissingRate = useMemo(() => {
    if (rows.length === 0) return 0;
    const missing = rows.filter(r => r.grossProfitValue === null).length;
    return missing / rows.length;
  }, [rows]);

  // Chart configs to satisfy ChartContainer context (colors & labels)
  const chartConfigTrend = useMemo(
    () => ({
      grossProfit: { label: language === 'zh' ? '利潤' : 'Profit', color: '#a855f7' },
      orderCount: { label: language === 'zh' ? '訂單數' : 'Orders', color: '#22d3ee' },
    }),
    [language]
  );

  const chartConfigNet = useMemo(
    () => ({
      netRevenue: { label: language === 'zh' ? '淨收入' : 'Net Revenue', color: '#a855f7' },
    }),
    [language]
  );

  const chartConfigPrice = useMemo(
    () => ({
      netRevenue: { label: language === 'zh' ? '淨收入' : 'Net Revenue', color: '#8b5cf6' },
      quantity: { label: language === 'zh' ? '件數' : 'Units', color: '#22d3ee' },
    }),
    [language]
  );

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    // 🔄 使用專業的刷新搜索機制
    console.log('🔄 觸發專業刷新搜索，條件:', filters);
    triggerReloadWithSalesAnalysisFilters(filters);
  };

  useEffect(() => {
    return () => {
      ctrlRef.current?.abort();
    };
  }, []);

  return (
    <div className="min-h-screen bg-background p-6">
      <div className="max-w-7xl mx-auto">
        <div className="flex items-center gap-4 mb-6">
          <Button
            variant="outline"
            onClick={() => {
              localStorage.setItem('selectedCategory', ANALYTICS_CATEGORY);
              navigate(ROUTES.dashboard.root);
            }}
            className="bg-slate-800/50 border-slate-600 text-slate-300 hover:bg-slate-700/50"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            {language === 'zh' ? '返回主控台' : 'Back to Dashboard'}
          </Button>
          <div className="flex items-center gap-2">
            <BarChart3 className="w-6 h-6 text-purple-400" />
            <h1 className="text-2xl font-bold bg-gradient-to-r from-purple-400 to-cyan-400 bg-clip-text text-transparent">{language === 'zh' ? '綜合統計 - 銷售分析' : 'Sales Analysis'}</h1>
          </div>
        </div>

        {/* Filters */}
        <Card className="bg-card/80 border-border mb-6">
          <CardHeader>
            <CardTitle className="text-foreground flex items-center gap-2">
              <Search className="w-5 h-5" />
              {language === 'zh' ? '搜尋條件' : 'Filters'}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit}>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
                <div>
                  <Label htmlFor="startDate" className="text-muted-foreground">{language === 'zh' ? '開始日期' : 'Start Date'}</Label>
                  <Input
                    id="startDate"
                    type="date"
                    value={filters.startDate}
                    onChange={(e) => setFilters(prev => ({ ...prev, startDate: e.target.value }))}
                    className="bg-background/50 border-input text-foreground"
                  />
                </div>
                <div>
                  <Label htmlFor="endDate" className="text-muted-foreground">{language === 'zh' ? '結束日期' : 'End Date'}</Label>
                  <Input
                    id="endDate"
                    type="date"
                    value={filters.endDate}
                    onChange={(e) => setFilters(prev => ({ ...prev, endDate: e.target.value }))}
                    className="bg-background/50 border-input text-foreground"
                  />
                </div>
                <div>
                  <Label htmlFor="skuId" className="text-muted-foreground">SKU</Label>
                  <Input
                    id="skuId"
                    value={filters.skuId}
                    onChange={(e) => setFilters(prev => ({ ...prev, skuId: e.target.value }))}
                    className="bg-background/50 border-input text-foreground"
                    placeholder={language === 'zh' ? '輸入SKU' : 'Enter SKU'}
                  />
                </div>
                <div>
                  <Label htmlFor="brand" className="text-muted-foreground">{language === 'zh' ? '品牌' : 'Brand'}</Label>
                  <Input
                    id="brand"
                    value={filters.brand}
                    onChange={(e) => setFilters(prev => ({ ...prev, brand: e.target.value }))}
                    className="bg-background/50 border-input text-foreground"
                    placeholder={language === 'zh' ? '輸入品牌' : 'Enter brand'}
                  />
                </div>
                <div>
                  <Label htmlFor="categoryCode" className="text-muted-foreground">{language === 'zh' ? '分類碼' : 'Category Code'}</Label>
                  <Input
                    id="categoryCode"
                    value={filters.categoryCode}
                    onChange={(e) => setFilters(prev => ({ ...prev, categoryCode: e.target.value }))}
                    className="bg-background/50 border-input text-foreground"
                    placeholder={language === 'zh' ? '輸入分類碼' : 'Enter category code'}
                  />
                </div>
                <div>
                  <Label htmlFor="productName" className="text-muted-foreground">{language === 'zh' ? '產品名稱' : 'Product Name'}</Label>
                  <Input
                    id="productName"
                    value={filters.productName}
                    onChange={(e) => setFilters(prev => ({ ...prev, productName: e.target.value }))}
                    className="bg-background/50 border-input text-foreground"
                    placeholder={language === 'zh' ? '輸入產品名稱' : 'Enter product name'}
                  />
                </div>
              </div>
              <div className="flex gap-2">
                <Button type="submit" disabled={loading} className="bg-purple-600 hover:bg-purple-700 disabled:opacity-50">
                  <Search className="w-4 h-4 mr-2" />
                  {loading ? (language === 'zh' ? '搜尋中...' : 'Searching...') : (language === 'zh' ? '搜尋' : 'Search')}
                </Button>
                <Button
                  type="button"
                  onClick={clearFilters}
                  disabled={loading}
                  variant="outline"
                  className="bg-card/50 border-border text-foreground hover:bg-secondary/50"
                >
                  <RotateCcw className="w-4 h-4 mr-2" />
                  {language === 'zh' ? '清除篩選' : 'Clear'}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>

        {/* KPI cards row 1 */}
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 mb-6">
          <Card className="bg-card/80 border-border">
            <CardHeader className="pb-2">
              <CardTitle className="text-muted-foreground text-sm">{language === 'zh' ? '交易金額 (GMV)' : 'GMV'}</CardTitle>
            </CardHeader>
            <CardContent className="text-2xl font-semibold text-foreground">${aggregations.totalAmount.toFixed(2)}</CardContent>
          </Card>
          <Card className="bg-card/80 border-border">
            <CardHeader className="pb-2">
              <CardTitle className="text-muted-foreground text-sm flex items-center gap-2"><TrendingUp className="w-4 h-4 text-purple-400" />{language === 'zh' ? '淨收入' : 'Net Revenue'}</CardTitle>
            </CardHeader>
            <CardContent className="text-2xl font-semibold text-foreground">${aggregations.netRevenue.toFixed(2)}</CardContent>
          </Card>
          <Card className="bg-card/80 border-border">
            <CardHeader className="pb-2">
              <CardTitle className="text-muted-foreground text-sm">{language === 'zh' ? '毛利額' : 'Gross Profit'}</CardTitle>
            </CardHeader>
            <CardContent className="text-2xl font-semibold text-foreground">
              {aggregations.grossProfitAvailability === 0 ? (language === 'zh' ? '暫無數據' : 'No data') : `$${aggregations.grossProfit.toFixed(2)}`}
              {grossProfitMissingRate > 0 && (
                <div className="text-xs text-amber-300 mt-1">
                  {language === 'zh' ? `毛利缺值 ${(grossProfitMissingRate * 100).toFixed(1)}%` : `Gross profit missing ${(grossProfitMissingRate * 100).toFixed(1)}%`}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* KPI cards row 2 */}
        <div className="grid grid-cols-1 md:grid-cols-3 xl:grid-cols-3 gap-4 mb-6">
          <Card className="bg-card/80 border-border">
            <CardHeader className="pb-2">
              <CardTitle className="text-muted-foreground text-sm">{language === 'zh' ? '訂單數 / 利潤' : 'Orders / Profit'}</CardTitle>
            </CardHeader>
            <CardContent className="text-foreground">
              <div className="text-2xl font-semibold">{aggregations.orderCount}</div>
              <div className="text-sm text-muted-foreground mt-1">{language === 'zh' ? '平均單筆利潤' : 'Avg Profit / Order'}: ${aggregations.profitPerOrder.toFixed(2)}</div>
            </CardContent>
          </Card>
          <Card className="bg-card/80 border-border">
            <CardHeader className="pb-2">
              <CardTitle className="text-muted-foreground text-sm">{language === 'zh' ? '件數 / 利潤' : 'Units / Profit'}</CardTitle>
            </CardHeader>
            <CardContent className="text-foreground">
              <div className="text-2xl font-semibold">{aggregations.quantity}</div>
              <div className="text-sm text-muted-foreground mt-1">{language === 'zh' ? '平均單件利潤' : 'Avg Profit / Unit'}: ${aggregations.profitPerUnit.toFixed(2)}</div>
            </CardContent>
          </Card>
          <Card className="bg-card/80 border-border">
            <CardHeader className="pb-2">
              <CardTitle className="text-muted-foreground text-sm">{language === 'zh' ? '訂單數 / 日數' : 'Orders / Day'}</CardTitle>
            </CardHeader>
            <CardContent className="text-foreground">
              <div className="text-2xl font-semibold">{daySpan > 0 ? daySpan : '-'}</div>
              <div className="text-sm text-muted-foreground mt-1">
                {language === 'zh' ? '平均每日訂單' : 'Avg orders/day'}: {aggregations.avgOrdersPerDay.toFixed(2)}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Trend */}
        <Card className="bg-card/80 border-border mb-6">
          <CardHeader>
            <CardTitle className="text-foreground flex items-center gap-2">
              <Activity className="w-5 h-5 text-purple-400" />
              {language === 'zh' ? '時間趨勢（利潤 / 訂單數）' : 'Trend (Profit / Orders)'}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {trendData.length === 0 ? (
              <div className="text-slate-400 text-sm">{language === 'zh' ? '沒有資料' : 'No data yet'}</div>
            ) : (
              <div className="w-full" style={{ minWidth: 800 }}>
                <ChartContainer className="h-96 w-full" config={chartConfigTrend}>
                  <ResponsiveContainer>
                    <LineChart data={trendData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                      <XAxis dataKey="date" stroke="#cbd5e1" />
                      <YAxis yAxisId="left" stroke="#cbd5e1" />
                      <YAxis yAxisId="right" orientation="right" stroke="#cbd5e1" />
                      <Tooltip content={<ChartTooltipContent />} />
                    <Legend content={<ChartLegendContent className="text-slate-200" />} />
                      <Line yAxisId="left" type="monotone" dataKey="grossProfit" name={language === 'zh' ? '利潤' : 'Profit'} stroke="#a855f7" strokeWidth={2} dot={false} />
                      <Line yAxisId="right" type="monotone" dataKey="orderCount" name={language === 'zh' ? '訂單數' : 'Orders'} stroke="#22d3ee" strokeWidth={2} dot={false} />
                    </LineChart>
                  </ResponsiveContainer>
                </ChartContainer>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Top SKU (full width with metric toggle) */}
        <Card className="bg-card/80 border-border mb-6">
          <CardHeader className="flex flex-col gap-3">
            <div className="flex items-center justify-between flex-wrap gap-3">
              <CardTitle className="text-foreground">{language === 'zh' ? 'Top 10 SKU' : 'Top 10 SKU'}</CardTitle>
              <div className="flex gap-2">
                <Button
                  size="sm"
                  variant={topSkuMetric === 'gmv' ? 'default' : 'outline'}
                  className={topSkuMetric === 'gmv' ? 'bg-purple-600 hover:bg-purple-700' : 'bg-card border-border text-foreground'}
                  onClick={() => setTopSkuMetric('gmv')}
                >
                  {language === 'zh' ? '交易總額' : 'GMV'}
                </Button>
                <Button
                  size="sm"
                  variant={topSkuMetric === 'grossProfit' ? 'default' : 'outline'}
                  className={topSkuMetric === 'grossProfit' ? 'bg-purple-600 hover:bg-purple-700' : 'bg-card border-border text-foreground'}
                  onClick={() => setTopSkuMetric('grossProfit')}
                >
                  {language === 'zh' ? '利潤' : 'Profit'}
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {topSkuData.length === 0 ? (
              <div className="text-muted-foreground text-sm">{language === 'zh' ? '沒有資料' : 'No data'}</div>
            ) : (
              <>
                <div className="w-full" style={{ minWidth: 800 }}>
                  <ChartContainer
                    className="h-72 w-full"
                    config={
                      topSkuMetric === 'gmv'
                        ? { gmv: { label: language === 'zh' ? '交易總額' : 'GMV', color: '#a855f7' } }
                        : { grossProfit: { label: language === 'zh' ? '利潤' : 'Profit', color: '#a855f7' } }
                    }
                  >
                    <ResponsiveContainer>
                      <BarChart data={topSkuData}>
                        {top3DGradients}
                        <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                        <XAxis dataKey="rank" tick={{ fill: '#cbd5e1', fontSize: 12 }} interval={0} angle={0} height={30} />
                        <YAxis tick={{ fill: '#cbd5e1' }} />
                    <Tooltip content={renderTopSkuTooltip} />
                        <Legend content={<ChartLegendContent className="text-slate-200" />} />
                        <Bar
                          dataKey={topSkuMetric === 'gmv' ? 'gmv' : 'grossProfit'}
                          name={
                            topSkuMetric === 'gmv'
                              ? language === 'zh' ? '交易總額' : 'GMV'
                              : language === 'zh' ? '利潤' : 'Profit'
                          }
                          fill="url(#topSku3dFront)"
                          radius={[12, 12, 4, 4]}
                          maxBarSize={80}
                          filter="url(#topSkuShadow3D)"
                        >
                          {topSkuData.map((entry, index) => (
                            <Cell
                              key={`cell-${entry.sku}`}
                              fill="url(#topSku3dFront)"
                              stroke={entry.color}
                              strokeWidth={1.5}
                            />
                          ))}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </ChartContainer>
                </div>

                {/* Top 10 details */}
                <div className="mt-4 text-foreground text-sm">
                  <div className="font-semibold mb-2">{language === 'zh' ? 'TOP 10 明細內容' : 'Top 10 Details'}</div>
                  <div className="overflow-x-auto">
                    <table className="min-w-full text-left text-sm">
                      <thead className="text-muted-foreground">
                        <tr className="border-b border-border">
                          <th className="py-2 pr-4">{language === 'zh' ? '排名' : 'Rank'}</th>
                          <th className="py-2 pr-4">SKU</th>
                          <th className="py-2 pr-4">{language === 'zh' ? '品牌' : 'Brand'}</th>
                          <th className="py-2 pr-4">{language === 'zh' ? '名稱' : 'Name'}</th>
                          <th className="py-2 pr-4">{language === 'zh' ? '總數量' : 'Total Qty'}</th>
                          <th className="py-2 pr-4">
                            {topSkuMetric === 'gmv' ? (language === 'zh' ? '交易總額' : 'GMV') : (language === 'zh' ? '總利潤' : 'Profit')}
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {topSkuData.map(item => (
                          <tr key={item.sku} className="border-b border-border hover:bg-accent/30">
                            <td className="py-2 pr-4 text-foreground" style={{ color: item.color }}>{`Top ${item.rank}`}</td>
                            <td className="py-2 pr-4 text-foreground">{item.sku}</td>
                            <td className="py-2 pr-4 text-foreground">{item.brand}</td>
                            <td className="py-2 pr-4 text-foreground">{item.name}</td>
                            <td className="py-2 pr-4 text-foreground">{item.quantity}</td>
                            <td className="py-2 pr-4 text-foreground">
                              {topSkuMetric === 'gmv'
                                ? `$${item.gmv.toFixed(2)}`
                                : `$${item.grossProfit.toFixed(2)}`}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </>
            )}
          </CardContent>
        </Card>

        {/* Brand Top 15 with metric toggle and details */}
        <Card className="bg-card/80 border-border mb-6">
          <CardHeader className="flex flex-col gap-3">
            <div className="flex items-center justify-between flex-wrap gap-3">
              <CardTitle className="text-foreground">{language === 'zh' ? 'Top 5 品牌' : 'Top 5 Brands'}</CardTitle>
              <div className="flex gap-2">
                <Button
                  size="sm"
                  variant={topBrandMetric === 'gmv' ? 'default' : 'outline'}
                  className={topBrandMetric === 'gmv' ? 'bg-purple-600 hover:bg-purple-700' : 'bg-card border-border text-foreground'}
                  onClick={() => setTopBrandMetric('gmv')}
                >
                  {language === 'zh' ? '交易總額' : 'GMV'}
                </Button>
                <Button
                  size="sm"
                  variant={topBrandMetric === 'grossProfit' ? 'default' : 'outline'}
                  className={topBrandMetric === 'grossProfit' ? 'bg-purple-600 hover:bg-purple-700' : 'bg-card border-border text-foreground'}
                  onClick={() => setTopBrandMetric('grossProfit')}
                >
                  {language === 'zh' ? '利潤' : 'Profit'}
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {topBrandData.length === 0 ? (
              <div className="text-muted-foreground text-sm">{language === 'zh' ? '沒有資料' : 'No data'}</div>
            ) : (
              <>
                <div className="w-full" style={{ minWidth: 800 }}>
                  <ChartContainer
                    className="h-64 w-full"
                    config={
                      topBrandMetric === 'gmv'
                        ? { gmv: { label: language === 'zh' ? '交易總額' : 'GMV', color: '#22d3ee' } }
                        : { grossProfit: { label: language === 'zh' ? '利潤' : 'Profit', color: '#22d3ee' } }
                    }
                  >
                    <ResponsiveContainer>
                      <BarChart data={topBrandData}>
                        {top3DGradients}
                        <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                        <XAxis dataKey="rank" tick={{ fill: '#cbd5e1', fontSize: 12 }} interval={0} angle={0} height={30} />
                        <YAxis tick={{ fill: '#cbd5e1' }} />
                        <Tooltip content={<ChartTooltipContent />} />
                        <Legend content={<ChartLegendContent className="text-slate-200" />} />
                        <Bar
                          dataKey={topBrandMetric === 'gmv' ? 'gmv' : 'grossProfit'}
                          name={
                            topBrandMetric === 'gmv'
                              ? language === 'zh' ? '交易總額' : 'GMV'
                              : language === 'zh' ? '利潤' : 'Profit'
                          }
                          fill="url(#topSku3dFront)"
                          radius={[12, 12, 4, 4]}
                          maxBarSize={80}
                          filter="url(#topSkuShadow3D)"
                        >
                          {topBrandData.map((entry) => (
                            <Cell
                              key={`cell-brand-${entry.name}`}
                              fill="url(#topSku3dFront)"
                              stroke={entry.color}
                              strokeWidth={1.5}
                            />
                          ))}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </ChartContainer>
                </div>

                <div className="mt-4 text-foreground text-sm">
                  <div className="font-semibold mb-2">{language === 'zh' ? 'TOP 5 明細內容' : 'Top 5 Details'}</div>
                  <div className="overflow-x-auto">
                    <table className="min-w-full text-left text-sm">
                      <thead className="text-muted-foreground">
                        <tr className="border-b border-border">
                          <th className="py-2 pr-4">{language === 'zh' ? '排名' : 'Rank'}</th>
                          <th className="py-2 pr-4">{language === 'zh' ? '品牌' : 'Brand'}</th>
                          <th className="py-2 pr-4">{language === 'zh' ? '總數量' : 'Total Qty'}</th>
                          <th className="py-2 pr-4">
                            {topBrandMetric === 'gmv' ? (language === 'zh' ? '交易總額' : 'GMV') : (language === 'zh' ? '總利潤' : 'Profit')}
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {topBrandData.map(item => (
                          <tr key={item.name} className="border-b border-border hover:bg-accent/30">
                            <td className="py-2 pr-4 text-foreground" style={{ color: item.color }}>{`Top ${item.rank}`}</td>
                            <td className="py-2 pr-4 text-foreground">{item.name}</td>
                            <td className="py-2 pr-4 text-foreground">{item.quantity}</td>
                            <td className="py-2 pr-4 text-foreground">
                              {topBrandMetric === 'gmv'
                                ? `$${item.gmv.toFixed(2)}`
                                : `$${item.grossProfit.toFixed(2)}`}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </>
            )}
          </CardContent>
        </Card>

        {/* Category Top 10 by occurrence */}
        <Card className="bg-card/80 border-border mb-6">
          <CardHeader>
            <CardTitle className="text-foreground">{language === 'zh' ? 'TOP 10 分類' : 'Top 10 Categories'}</CardTitle>
          </CardHeader>
          <CardContent>
            {topCategoryData.length === 0 ? (
              <div className="text-muted-foreground text-sm">{language === 'zh' ? '沒有資料' : 'No data'}</div>
            ) : (
              <>
                <div className="w-full" style={{ minWidth: 800 }}>
                  <ChartContainer
                    className="h-64 w-full"
                    config={{ quantity: { label: language === 'zh' ? '數量' : 'Quantity', color: '#f59e0b' } }}
                  >
                    <ResponsiveContainer>
                      <BarChart data={topCategoryData}>
                        {top3DGradients}
                        <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                        <XAxis dataKey="rank" tick={{ fill: '#cbd5e1', fontSize: 12 }} interval={0} angle={0} height={30} />
                        <YAxis tick={{ fill: '#cbd5e1' }} />
                        <Tooltip
                          contentStyle={{ backgroundColor: '#0f172a', border: '1px solid #f59e0b' }}
                          formatter={(value: any, name: any, props: any) => {
                            return [value, name];
                          }}
                          labelFormatter={(label: any, payload: any) => payload?.[0]?.payload?.name || payload?.[0]?.payload?.code || label}
                        />
                        <Legend content={<ChartLegendContent className="text-slate-200" />} />
                        <Bar
                          dataKey="quantity"
                          name={language === 'zh' ? '數量' : 'Quantity'}
                          fill="url(#topSku3dFront)"
                          radius={[12, 12, 4, 4]}
                          maxBarSize={80}
                          filter="url(#topSkuShadow3D)"
                        >
                          {topCategoryData.map((entry) => (
                            <Cell
                              key={`cell-cat-${entry.code}`}
                              fill="url(#topSku3dFront)"
                              stroke={entry.color}
                              strokeWidth={1.5}
                            />
                          ))}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </ChartContainer>
                </div>

                <div className="mt-4 text-foreground text-sm">
                  <div className="font-semibold mb-2">{language === 'zh' ? 'TOP 10 明細內容' : 'Top 10 Details'}</div>
                  <div className="overflow-x-auto">
                    <table className="min-w-full text-left text-sm">
                      <thead className="text-muted-foreground">
                        <tr className="border-b border-border">
                          <th className="py-2 pr-4">{language === 'zh' ? '排名' : 'Rank'}</th>
                          <th className="py-2 pr-4">{language === 'zh' ? '分類碼' : 'Category Code'}</th>
                          <th className="py-2 pr-4">{language === 'zh' ? '分類項目' : 'Category Item'}</th>
                          <th className="py-2 pr-4">{language === 'zh' ? '分類內容' : 'Category Name'}</th>
                          <th className="py-2 pr-4">{language === 'zh' ? '數量' : 'Quantity'}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {topCategoryData.map(item => (
                          <tr key={item.code} className="border-b border-border hover:bg-accent/30">
                            <td className="py-2 pr-4 text-foreground" style={{ color: item.color }}>{`Top ${item.rank}`}</td>
                            <td className="py-2 pr-4 text-foreground">{item.code}</td>
                            <td className="py-2 pr-4 text-foreground">{item.subCategoryName || '-'}</td>
                            <td className="py-2 pr-4 text-foreground">{item.name || '-'}</td>
                            <td className="py-2 pr-4 text-foreground">{item.quantity}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </>
            )}
          </CardContent>
        </Card>

        {/* Price bands - full width */}
        <Card className="bg-card/80 border-border mb-6">
          <CardHeader className="flex flex-col gap-3">
            <div className="flex items-center justify-between flex-wrap gap-3">
              <CardTitle className="text-foreground flex items-center gap-2">
                <PieChart className="w-5 h-5 text-purple-400" />
                {language === 'zh' ? '價格帶分佈' : 'Price Band Distribution'}
              </CardTitle>
              <div className="flex gap-2">
                <Button
                  size="sm"
                  variant={priceBandMetric === 'price' ? 'default' : 'outline'}
                  className={priceBandMetric === 'price' ? 'bg-purple-600 hover:bg-purple-700' : 'bg-card border-border text-foreground'}
                  onClick={() => setPriceBandMetric('price')}
                >
                  {language === 'zh' ? '價格' : 'Price'}
                </Button>
                <Button
                  size="sm"
                  variant={priceBandMetric === 'profit' ? 'default' : 'outline'}
                  className={priceBandMetric === 'profit' ? 'bg-purple-600 hover:bg-purple-700' : 'bg-card border-border text-foreground'}
                  onClick={() => setPriceBandMetric('profit')}
                >
                  {language === 'zh' ? '利潤' : 'Profit'}
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <ChartContainer
              className="h-80 w-full"
              config={{
                gmv: { label: language === 'zh' ? '交易總額' : 'GMV', color: '#8b5cf6' },
                quantity: { label: language === 'zh' ? '件數' : 'Units', color: '#22d3ee' },
                profit: { label: language === 'zh' ? '利潤' : 'Profit', color: '#f59e0b' }
              }}
            >
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={priceBandData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                  <XAxis
                    dataKey="label"
                    tick={{ fill: '#cbd5e1', fontSize: 12 }}
                    angle={-45}
                    textAnchor="end"
                    height={80}
                  />
                  <YAxis yAxisId="left" orientation="left" tick={{ fill: '#cbd5e1' }} />
                  <YAxis yAxisId="right" orientation="right" tick={{ fill: '#cbd5e1' }} />
                  <Tooltip content={<ChartTooltipContent />} />
                  <Legend content={<ChartLegendContent className="text-slate-200" />} />
                  {priceBandMetric === 'price' ? (
                    <>
                      <Bar
                        yAxisId="left"
                        dataKey="gmv"
                        name={language === 'zh' ? '交易總額' : 'GMV'}
                        fill="url(#priceBand3dFront)"
                        radius={[8, 8, 4, 4]}
                        maxBarSize={60}
                      />
                      <Bar
                        yAxisId="right"
                        dataKey="quantity"
                        name={language === 'zh' ? '件數' : 'Units'}
                        fill="url(#priceBand3dSide)"
                        radius={[8, 8, 4, 4]}
                        maxBarSize={60}
                      />
                    </>
                  ) : (
                    <>
                      <Bar
                        yAxisId="left"
                        dataKey="profit"
                        name={language === 'zh' ? '利潤' : 'Profit'}
                        fill="url(#priceBand3dFront)"
                        radius={[8, 8, 4, 4]}
                        maxBarSize={60}
                      />
                      <Bar
                        yAxisId="right"
                        dataKey="quantity"
                        name={language === 'zh' ? '件數' : 'Units'}
                        fill="url(#priceBand3dSide)"
                        radius={[8, 8, 4, 4]}
                        maxBarSize={60}
                      />
                    </>
                  )}
                  <defs>
                    <linearGradient id="priceBand3dFront" x1="0" y1="0" x2="1" y2="1">
                      <stop offset="0%" stopColor="#8b5cf6" stopOpacity={1}/>
                      <stop offset="50%" stopColor="#7c3aed" stopOpacity={0.95}/>
                      <stop offset="100%" stopColor="#5b21b6" stopOpacity={0.85}/>
                    </linearGradient>
                    <linearGradient id="priceBand3dSide" x1="0" y1="0" x2="1" y2="0">
                      <stop offset="0%" stopColor="#22d3ee" stopOpacity={1}/>
                      <stop offset="50%" stopColor="#0891b2" stopOpacity={0.95}/>
                      <stop offset="100%" stopColor="#0e7490" stopOpacity={0.85}/>
                    </linearGradient>
                    <filter id="priceBandShadow3D" x="-50%" y="-50%" width="200%" height="200%">
                      <feGaussianBlur in="SourceAlpha" stdDeviation="3"/>
                      <feOffset dx="2" dy="4" result="offsetblur"/>
                      <feComponentTransfer>
                        <feFuncA type="linear" slope="0.3"/>
                      </feComponentTransfer>
                      <feMerge>
                        <feMergeNode/>
                        <feMergeNode in="SourceGraphic"/>
                      </feMerge>
                    </filter>
                  </defs>
                </BarChart>
              </ResponsiveContainer>
            </ChartContainer>
          </CardContent>
        </Card>

        {/* Details table removed per requirement */}
      </div>
    </div>
  );
};

export default SalesAnalysis;
